import type { FC, ReactNode } from "react";
import { Switch as LibSwitch } from "@headlessui/react";
import { cx } from "../../../utils/classnames";

export enum LabelPosition {
  Left = "left",
  Right = "right"
}

export interface SwitchProps {
  checked: boolean;
  label?: ReactNode;
  onChange: (v: boolean) => void;
  className?: string;
  disabled?: boolean;
  wrapperClassName?: string;
  position?: LabelPosition;
}

const LabelWrapper: FC<{
  label?: ReactNode;
  position?: LabelPosition;
  children: ReactNode;
}> = ({ label, position = LabelPosition.Left, children }) => {
  if (!label) return <>{children}</>;

  return (
    <>
      {position === LabelPosition.Left ? <>{label}</> : null}
      {children}
      {position === LabelPosition.Right ? <>{label}</> : null}
    </>
  );
};

export const Switch: FC<SwitchProps> = ({
  checked,
  label,
  onChange,
  className,
  disabled,
  wrapperClassName,
  position = LabelPosition.Right
}) => {
  return (
    <LibSwitch.Group as="div" className="space-y-4">
      <div className={cx("flex items-center gap-3", wrapperClassName)}>
        <LabelWrapper
          label={
            label ? (
              <LibSwitch.Description
                as="p"
                className={className ?? "text-sm font-medium text-content-1000"}
              >
                {label}
              </LibSwitch.Description>
            ) : null
          }
          position={position}
        >
          <LibSwitch
            checked={checked}
            className={cx(
              checked ? "bg-primary-500" : "bg-alpha-100",
              "shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none"
            )}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              onChange(!checked);
            }}
          >
            <span
              aria-hidden="true"
              className={`${
                checked ? "translate-x-6" : "translate-x-1"
              } inline-block shadow-sm h-4 w-4 transform rounded-full bg-white transition-transform`}
            />
          </LibSwitch>
        </LabelWrapper>
      </div>
    </LibSwitch.Group>
  );
};
